import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Loader from "../../../common/Loader";
import ServicesComponent from "./ServicesComponent";
import styles from "./Services.module.css";
import ServiceProviderFilter from "../../filters/ServiceProviderFilter";
import { getPublicServices } from "../../../../api/user/serviceProvider/service-provider";
import { parseSearchQuery, matchesParsedQuery } from "../../../../utils/parseSearchQuery";

const Services = () => {
  const { t } = useTranslation();
  const [showFilterP, setShowFilterP] = useState(false);
  const [filters, setFilters] = useState({});
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await getPublicServices(filters);

      if (result.success) {
        const servicesData = result.data || result.services || [];
        setServices(servicesData);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(t("services_page.load_error"));
    } finally {
      setLoading(false);
    }
  };

  // Extract unique category names from fetched services
  const categories = [...new Set(
    services
      .map(s => s.service?.name)
      .filter(Boolean)
  )].sort();

  // function to toggle filter display
  function toggleFilter() {
    setShowFilterP(!showFilterP);
  }

  // Apply filters from the filter panel
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setActiveFilterCount(
      Object.values(newFilters).filter(v => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)).length
    );
    setShowFilterP(false);
  };

  const handleClearFilters = () => {
    setFilters({});
    setActiveFilterCount(0);
  };

  // Client-side search: smart parse for natural language like "plombier près de douala"
  const displayedServices = services.filter((offer) => {
    if (!searchQuery.trim()) return true;
    const parsed = parseSearchQuery(searchQuery);
    return matchesParsedQuery(offer, parsed, {
      name: (o) => o.title || '',
      location: (o) => o.location || o.userService?.location || '',
      description: (o) => o.description || '',
      category: (o) => o.service?.name || '',
    });
  });

  return (
    <section className={styles.servicesListingSection}>
      {/* ========================= HERO ========================= */}
      <div className={styles.servicesPageHero}>
        <span className={styles.servicesPageEyebrow}>
          <i className="fa fa-tools" aria-hidden="true" />
          {t("services_page.eyebrow")}
        </span>
        <h1 className={styles.servicesPageTitle}>{t("services_page.title")}</h1>
        <p className={styles.servicesPageSubtitle}>{t("services_page.subtitle")}</p>
        <div className={styles.servicesPageFlagline} aria-hidden="true">
          <span></span><span></span><span></span>
        </div>
      </div>

      {showFilterP && (
        <ServiceProviderFilter
          toggleFilter={toggleFilter}
          currentFilters={filters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          categories={categories}
        />
      )}

      {/* ====================== TOOLBAR ========================= */}
      <div className={styles.listingsTop}>
        <div className={styles.toolbarSearchWrap}>
          <i className="fa fa-search" aria-hidden="true" style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--sf-muted)', fontSize: '0.85rem', pointerEvents: 'none' }} />
          <input
            type="text"
            className={styles.toolbarSearchInput}
            placeholder={t('services_page.search_placeholder', 'Search services...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className={styles.toolbarSearchClear} onClick={() => setSearchQuery('')} aria-label="Clear search">
              <i className="fa fa-times" aria-hidden="true"></i>
            </button>
          )}
        </div>
        <div className={styles.toolbarActions}>
          {activeFilterCount > 0 && (
            <button onClick={handleClearFilters} className={styles.clearFiltersButton}>
              <i className="fa fa-times" aria-hidden="true"></i>
              <span className={styles.clearFiltersLabel}>{t('service_filter.clear')}</span>
            </button>
          )}
          <button onClick={toggleFilter} className={styles.filterButton}>
            <i className="fas fa-sliders-h" aria-hidden="true"></i>
            <span className={styles.filterBtnLabel}>{t('services_page.filters')}</span>
            {activeFilterCount > 0 && <span className={styles.filterCount}>{activeFilterCount}</span>}
          </button>
        </div>
      </div>

      {/* Active search indicator */}
      {searchQuery && (
        <div className={styles.activeSearchBar}>
          <i className="fa fa-search" aria-hidden="true" />
          <span>{t('services_page.results_for', 'Results for')} "{searchQuery}"</span>
          <span className={styles.searchResultCount}>{displayedServices.length} {t('services_page.found', 'found')}</span>
        </div>
      )}

      {/* ======================= CONTENT ======================== */}
      <div className={styles.allListings}>
        {loading ? (
          <div className={styles.stateBlock}>
            <Loader label={t("services_page.loading")} />
          </div>
        ) : error ? (
          <div className={`${styles.stateBlock} ${styles.stateError}`}>
            <i className="fa fa-exclamation-triangle" aria-hidden="true"></i>
            <p>{error}</p>
            <button onClick={fetchServices} className={styles.retryButton}>
              <i className="fa fa-redo" aria-hidden="true"></i>
              {t("account_billing.retry")}
            </button>
          </div>
        ) : services.length === 0 ? (
          <div className={styles.stateBlock}>
            <i className="fa fa-search" aria-hidden="true"></i>
            <p>{searchQuery ? t('services_page.no_search_results', 'No results found') : t('services_page.no_services')}</p>
            <span>{searchQuery ? t('services_page.try_different_search', 'Try a different search term') : t('services_page.no_services_desc')}</span>
          </div>
        ) : (
          <div className={styles.listingsGrid}>
            {displayedServices.map((offer, index) => (
              <div
                key={offer._id}
                className={styles.cardReveal}
                style={{ animationDelay: `${Math.min(index * 60, 600)}ms` }}
              >
                <ServicesComponent
                  serviceId={offer._id}
                  name={offer.title}
                  description={offer.description}
                  fee={offer.price}
                  priceUnit={offer.priceUnit}
                  category={offer.service?.name}
                  location={
                    offer.location ||
                    offer.userService?.location ||
                    t("services_page.location_not_specified")
                  }
                  images={offer.images}
                  availability={t("services_page.available")}
                  isProviderActive={offer.userService?.isActive ?? false}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;
